/**
 * Снимок и план стола для единого call. Сама отправка — `deskCall` → `/v1/me/call`.
 * Старая ручка `/v1/calls/share` остаётся для тестов и прямого вызова.
 */

import { Decimal } from 'decimal.js';

import type { Fork } from '../lib/protocol';
import { capsInput, tempoOf, useSettings } from '../store/settings';
import { rowWithLock, type FeedRow } from './rows';
import { useScreen } from './store';

export interface ShareResult {
  ok: boolean;
  callId?: string;
  messageId?: number | null;
  status?: 'sent' | 'edited' | 'same';
  detail: string;
}

export interface DeskPlan {
  roiNet: string;
  bank: string;
  total: string;
  fx: string;
  cur: 'USD';
  legs: Array<{
    venue: string;
    url: string;
    event_id: string;
    selection: string;
    market: string;
    participants: string[];
    name: string;
    currency: string;
    stake: string;
    debit: string;
    payout: string;
    odds: string;
    price: string | null;
    shares: string | null;
  }>;
}

let canShareCache: boolean | null = null;

export function resetDeskShare(): void {
  canShareCache = null;
}

export async function deskCanShare(): Promise<boolean> {
  if (canShareCache !== null) return canShareCache;
  try {
    const response = await fetch('/v1/calls/desk', { credentials: 'same-origin' });
    if (!response.ok) {
      canShareCache = false;
      return false;
    }
    const body = (await response.json()) as { canShare?: unknown };
    canShareCache = body.canShare === true;
    return canShareCache;
  } catch {
    canShareCache = false;
    return false;
  }
}

function say(status: string | undefined): string {
  if (status === 'edited') return 'обновили карточку в ветке';
  if (status === 'same') return 'уже в ветке, цифры те же';
  if (status === 'sent') return 'отправили в ветку';
  return 'отправили';
}

function detailOf(body: { detail?: unknown }): string {
  return typeof body.detail === 'string' && body.detail.trim() !== ''
    ? body.detail
    : 'не удалось отправить';
}

function moneyPlain(value: Decimal): string {
  return value.toDecimalPlaces(2, Decimal.ROUND_DOWN).toFixed(2);
}

export function calcRowOf(item: FeedRow): FeedRow {
  const settings = useSettings.getState();
  const screen = useScreen.getState();
  return rowWithLock(item, {
    bank: settings.bank,
    fx: settings.fx,
    ...capsInput(settings),
    tempo: tempoOf(settings.attempts),
    roundStep: settings.roundStep,
    roundDir: settings.roundDir,
    roundWho: settings.roundWho,
    lock: screen.stakeLock ?? undefined,
    odds: screen.oddsLock ?? undefined,
    fees: screen.feeLock ?? undefined,
    curs: screen.curLock ?? undefined,
    free: screen.freeLock ?? undefined,
    hand: screen.handLock ?? undefined,
    hold: true,
  });
}

export function forkFromCalc(row: FeedRow): Fork {
  const fork = row.row.fork;
  const legs = Array.isArray(fork.legs)
    ? fork.legs.map((leg, index) => {
        const calc = row.row.legs[index];
        if (calc === undefined) return leg;
        const share =
          calc.cents !== null ? new Decimal(calc.cents).div(100).toFixed() : leg.share;
        return { ...leg, odds: calc.odds, share };
      })
    : fork.legs;
  return { ...fork, id: row.id, roi: String(row.row.roiPct), legs };
}

export function planFromCalc(row: FeedRow, fx: number): DeskPlan | null {
  const legs = row.row.legs.slice(0, 2);
  if (legs.length < 2) return null;
  const rate = new Decimal(fx > 0 ? fx : 90);
  const coupons = legs.map((leg, index) => {
    const rubStake = new Decimal(leg.rub);
    const usdStake = leg.usd !== null ? new Decimal(leg.usd) : rubStake.div(rate);
    const rubPay = new Decimal(leg.payout);
    const rubCur = leg.usd === null;
    const stake = rubCur ? rubStake : usdStake;
    const payout = rubCur ? rubPay : rubPay.div(rate);
    const price = leg.cents !== null ? new Decimal(leg.cents).div(100) : null;
    const shares = leg.shares !== null ? new Decimal(leg.shares) : null;
    const src = row.row.fork.legs?.[index];
    return {
      venue: leg.source,
      url: String(src?.betUrl || src?.url || leg.url || ''),
      event_id: String(src?.eid || ''),
      selection: String(src?.sel || src?.asset || ''),
      market: String(row.row.fork.market || ''),
      participants: leg.who.length > 0 ? leg.who : (src?.participants ?? []),
      name: leg.name,
      currency: rubCur ? 'RUB' : 'USD',
      stake: moneyPlain(stake),
      debit: moneyPlain(stake),
      payout: moneyPlain(payout),
      odds: new Decimal(leg.odds).toDecimalPlaces(3, Decimal.ROUND_DOWN).toFixed(3),
      price: price === null || !price.gt(0) ? null : price.toDecimalPlaces(4, Decimal.ROUND_DOWN).toFixed(4),
      shares: shares === null || !shares.gt(0) ? null : shares.toDecimalPlaces(4, Decimal.ROUND_DOWN).toFixed(4),
    };
  });
  const profit = new Decimal(row.row.profit);
  const staked = new Decimal(row.row.staked);
  const roi = staked.gt(0) ? profit.div(staked) : new Decimal(0);
  const bank = coupons.reduce((sum, one) => {
    const stake = new Decimal(one.stake);
    return one.currency === 'RUB' ? sum.plus(stake.div(rate)) : sum.plus(stake);
  }, new Decimal(0));
  return {
    roiNet: roi.toFixed(),
    bank: moneyPlain(bank),
    total: moneyPlain(bank),
    fx: rate.toFixed(),
    cur: 'USD',
    legs: coupons,
  };
}

export async function shareFork(
  forkId: string,
  fork?: object,
  plan?: object,
): Promise<ShareResult> {
  try {
    const payload: { forkId: string; fork?: object; plan?: object } = { forkId };
    if (fork !== undefined) payload.fork = fork;
    if (plan !== undefined) payload.plan = plan;
    const response = await fetch('/v1/calls/share', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const body = (await response.json().catch(() => ({}))) as {
      detail?: unknown;
      callId?: string;
      messageId?: number | null;
      status?: 'sent' | 'edited' | 'same';
      ok?: boolean;
    };
    if (!response.ok) {
      return { ok: false, detail: detailOf(body) };
    }
    return {
      ok: true,
      callId: body.callId,
      messageId: body.messageId,
      status: body.status,
      detail: say(body.status),
    };
  } catch {
    return { ok: false, detail: 'сеть не ответила' };
  }
}

export function shareCalc(item: FeedRow): Promise<ShareResult> {
  const fx = useSettings.getState().fx;
  const card = calcRowOf(item);
  const plan = planFromCalc(card, fx > 0 ? fx : 90);
  if (plan === null) return Promise.resolve({ ok: false, detail: 'нет двух ног' });
  return shareFork(card.id, forkFromCalc(card), plan);
}
